import { FocusOfferingFormData } from '../models/offering';
import { FocusKnowledgeForm } from '../models/knowledge';
import {
  MulesoftKnowledgeContent,
  MulesoftOffering,
} from '../models/mulesoft-offering';

/**
 * Maps a Mulesoft Process-API offering/knowledge into the Focus form view models
 * (`FocusOfferingFormData` / `FocusKnowledgeForm`). These mirror the structure
 * produced by the (now-removed) `mapStoryblok*ToForm` mappers — the form controls
 * are unchanged — but read from the Mulesoft nested domain shape and are the
 * inverse of the write mappers in `mulesoft.mapper.ts`.
 */

/** Coerce API truthy values (boolean or "true"/"false" strings) to boolean. */
function coerceBool(value: unknown): boolean {
  return value === true || value === 'true';
}

/**
 * Tri-state coercion used by `isSupported`: true / false / '' (unset) — matches
 * the legacy Storyblok mapper behaviour.
 */
function coerceTriState(value: unknown): boolean | '' {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return '';
}

/** SSO providers is a free-text field in the form; flatten any array to a string. */
function ssoToString(
  providers: string[] | string | null | undefined,
): string {
  if (providers == null) return '';
  return Array.isArray(providers) ? providers.join(', ') : providers;
}

function mapHosting(o: MulesoftOffering) {
  return (o.hosting ?? [])
    .map((h) => ({
      name: h.hostingName ?? '',
      type: h.hostingType ?? '',
      managed: h.hostingManaged ?? '',
    }))
    .filter((h) => h.name || h.type || h.managed);
}

function mapAgents(o: MulesoftOffering) {
  return (o.agent ?? [])
    .map((a) => ({ name: a.agentName ?? '', url: a.agentUrl ?? '' }))
    .filter((a) => a.name || a.url);
}

function mapCommunities(o: MulesoftOffering) {
  return (o.community ?? [])
    .map((c) => ({
      title: c.title ?? '',
      description: c.description ?? '',
      url: c.url ?? '',
    }))
    .filter((c) => c.title || c.description || c.url);
}

function mapSupportChannels(o: MulesoftOffering) {
  return (o.supportChannels ?? [])
    .map((sc) => ({
      channelType: sc.channelType ?? '',
      destinationValue: sc.destinationValue ?? '',
      description: sc.description ?? '',
      serviceDeskName: sc.serviceDeskName ?? '',
    }))
    .filter((sc) => sc.channelType || sc.destinationValue);
}

function mapSupportHours(o: MulesoftOffering) {
  return (o.supportHours ?? [])
    .map((sh) => ({
      days: sh.days ?? [],
      startTime: sh.startTime ?? '',
      endTime: sh.endTime ?? '',
      timezone: sh.timezone ?? '',
      responseTime: sh.responseTime ?? '',
    }))
    .filter((sh) => (sh.days?.length ?? 0) > 0 || sh.startTime || sh.endTime);
}

function mapAvailableVersions(o: MulesoftOffering) {
  return (o.availableVersions ?? [])
    .map((av) => ({
      version: av.version ?? '',
      status: av.status ?? '',
      compatibility: av.compatibility ?? '',
      releaseDate: av.releaseDate ?? '',
      endOfLifeDate: av.endOfLifeDate ?? '',
      additionalInfo: av.additionalInfo ?? '',
    }))
    .filter((av) => av.version || av.status || av.compatibility);
}

function mapServicesAndRequests(o: MulesoftOffering) {
  return (o.servicesAndRequests ?? [])
    .map((req) => ({
      id: req.id ?? '',
      type: req.type ?? '',
      title: req.title ?? '',
      description: req.description ?? '',
      destinationValue: req.destinationValue ?? '',
    }))
    .filter((req) => req.type || req.title || req.description);
}

function mapOnboardingSteps(o: MulesoftOffering) {
  return (o.onboarding?.installationSteps ?? [])
    .map((step) => ({
      description: step.description ?? '',
      referenceUrl: step.referenceUrl ?? '',
    }))
    .filter((step) => step.description || step.referenceUrl);
}

function mapFeatures(o: MulesoftOffering) {
  return (o.features ?? [])
    .map((f) => ({
      name: f.name ?? '',
      description: f.description ?? '',
      benefit: f.benefit ?? '',
    }))
    .filter((f) => f.name || f.description);
}

function mapCapabilities(o: MulesoftOffering) {
  return (o.capabilities ?? [])
    .map((c) => ({
      name: c.name ?? '',
      description: c.description ?? '',
      benefit: c.benefit ?? '',
    }))
    .filter((c) => c.name || c.description);
}

function mapUseCases(o: MulesoftOffering) {
  return (o.useCases ?? [])
    .map((uc) => ({ name: uc.name ?? '', description: uc.description ?? '' }))
    .filter((uc) => uc.name || uc.description);
}

function mapPermissions(o: MulesoftOffering) {
  return {
    ownerEmail: o.contentOwnership?.ownerEmail?.trim() ?? '',
    deputyEmail: o.contentOwnership?.deputyEmail?.trim() ?? '',
    authorEmails: (o.contentOwnership?.authorEmails ?? []).map((e) => e.trim()),
  };
}

function perUserCostValueToString(o: MulesoftOffering): string {
  const value = o.cost?.perUserCost?.perUserCostValue;
  return value != null ? String(value) : '';
}

export function mapMulesoftOfferingToApplicationForm(
  o: MulesoftOffering,
): FocusOfferingFormData {
  return {
    id: String(o.id ?? ''),
    knowledge: o.knowledge ?? '',
    overview: {
      displayName: o.displayName ?? '',
      approvalStatus: o.approvalStatus ?? '',
      tags: o.tags?.value ?? [],
      successor: o.successor ?? '',
      description: o.description ?? '',
      offeringImage: o.offeringImage ?? '',
      callToAction: (o as { callToAction?: string }).callToAction ?? '',
      similarOfferings: o.similarOfferings ?? [],
    },
    details: {
      installationInformation: (o.installationInformation ?? [])
        .map((i) => ({
          applicationType: i.applicationType ?? '',
          instructions: i.instructions ?? '',
          url: i.url ?? '',
        }))
        .filter((i) => i.applicationType || i.instructions || i.url),
      systemRiskAssessmentName:
        o.systemRiskAssessment?.systemRiskAssessmentName ?? '',
      classificationLevel: o.classification?.classificationLevel ?? '',
      classificationRetentionPeriod:
        o.classification?.classificationRetentionPeriod ?? '',
      gxp: o.gxP ?? false,
      isPerUserPricingAvailable: coerceBool(o.cost?.isPerUserPricingAvailable),
      perUserCostValue: perUserCostValueToString(o),
      perUserCostCurrency: o.cost?.perUserCost?.perUserCostCurrency ?? '',
      perUserCostFrequency: o.cost?.perUserCost?.perUserCostFrequency ?? '',
      isOtherPricingModelsAvailable: !!o.otherPricingModels?.details,
      otherPricingModels: o.otherPricingModels?.details ?? '',
      isAdditionalCostConsiderationsAvailable:
        !!o.additionalCost?.additionalCostConsiderations,
      additionalCostConsiderations:
        o.additionalCost?.additionalCostConsiderations ?? '',
      licenseCostResponsibility: o.licenseCostResponsibility ?? '',
      isSingleSignOnSupported: coerceBool(
        o.singleSignOn?.isSingleSignOnSupported,
      ),
      singleSignOnProviders: ssoToString(o.singleSignOn?.singleSignOnProviders),
      isSupported: coerceTriState(o.isSupported),
      supportChannels: mapSupportChannels(o),
      supportHours: mapSupportHours(o),
      hosting: mapHosting(o),
      agents: mapAgents(o),
      communities: mapCommunities(o),
    },
    functionality: {
      features: mapFeatures(o),
      useCases: mapUseCases(o),
    },
    permissions: mapPermissions(o),
  };
}

export function mapMulesoftOfferingToInfrastructureForm(
  o: MulesoftOffering,
): FocusOfferingFormData {
  return {
    id: String(o.id ?? ''),
    knowledge: o.knowledge ?? '',
    overview: {
      displayName: o.displayName ?? '',
      approvalStatus: o.approvalStatus ?? '',
      successor: o.successor ?? '',
      description: o.description ?? '',
      similarOfferings: o.similarOfferings ?? [],
    },
    details: {
      qualificationStatus: o.qualificationStatus ?? '',
      qualificationDocumentation: (o.qualificationDocumentation ?? [])
        .map((qd) => ({ name: qd.documentName ?? '', url: qd.documentUrl ?? '' }))
        .filter((qd) => qd.name || qd.url),
      availableVersions: mapAvailableVersions(o),
      supportChannels: mapSupportChannels(o),
      supportHours: mapSupportHours(o),
      environments: (o.environment ?? [])
        .map((env) => ({
          name: env.environmentName ?? '',
          url: env.environmentUrl ?? '',
          serviceLevelAgreement: env.serviceLevelAgreement ?? '',
        }))
        .filter((env) => env.name || env.url),
      servicesAndRequests: mapServicesAndRequests(o),
      systemRiskAssessmentName:
        o.systemRiskAssessment?.systemRiskAssessmentName ?? '',
      classificationLevel: o.classification?.classificationLevel ?? '',
      isPerUserPricingAvailable: o.cost?.isPerUserPricingAvailable ?? false,
      perUserCostValue: perUserCostValueToString(o),
      perUserCostCurrency: o.cost?.perUserCost?.perUserCostCurrency ?? '',
      perUserCostFrequency: o.cost?.perUserCost?.perUserCostFrequency ?? '',
      isOtherPricingModelsAvailable: !!o.otherPricingModels?.details,
      otherPricingModels: o.otherPricingModels?.details ?? '',
      isAdditionalCostConsiderationsAvailable:
        !!o.additionalCost?.additionalCostConsiderations,
      additionalCostConsiderations:
        o.additionalCost?.additionalCostConsiderations ?? '',
      developmentCostResponsibility:
        o.developmentCostResponsibility?.responsibility ?? '',
      developmentCostResponsibilityOtherDetails:
        o.developmentCostResponsibility?.otherDetails ?? '',
      supportMaintenanceCostResponsibility:
        o.supportMaintenanceCostResponsibility?.responsibility ?? '',
      supportMaintenanceCostResponsibilityOtherDetails:
        o.supportMaintenanceCostResponsibility?.otherDetails ?? '',
      licenseCostResponsibility: o.licenseCostResponsibility ?? '',
      isSingleSignOnSupported: o.singleSignOn?.isSingleSignOnSupported ?? false,
      singleSignOnProviders: ssoToString(o.singleSignOn?.singleSignOnProviders),
      isSupported: o.isSupported ?? '',
      hosting: mapHosting(o),
      agents: mapAgents(o),
      communities: mapCommunities(o),
    },
    functionality: {
      features: mapFeatures(o),
      capabilities: mapCapabilities(o),
      useCases: mapUseCases(o),
    },
    permissions: mapPermissions(o),
  };
}

export function mapMulesoftOfferingToPlatformForm(
  o: MulesoftOffering,
): FocusOfferingFormData {
  return {
    id: String(o.id ?? ''),
    knowledge: o.knowledge ?? '',
    overview: {
      displayName: o.displayName ?? '',
      approvalStatus: o.approvalStatus ?? '',
      successor: o.successor ?? '',
      description: o.description ?? '',
      relationshipType: o.offeringRelationship?.relationshipType ?? '',
      relationshipTargetOfferingId:
        o.offeringRelationship?.targetOfferingId ?? '',
      similarOfferings: o.similarOfferings ?? [],
    },
    details: {
      onboardingAccessInstructions: o.onboarding?.accessInstructions ?? '',
      onboardingSteps: mapOnboardingSteps(o),
      availableVersions: mapAvailableVersions(o),
      supportChannels: mapSupportChannels(o),
      supportHours: mapSupportHours(o),
      environments: (o.environment ?? [])
        .map((env) => ({
          name: env.environmentName ?? '',
          url: env.environmentUrl ?? '',
          serviceLevelAgreement: env.serviceLevelAgreement ?? '',
        }))
        .filter((env) => env.name || env.url),
      servicesAndRequests: mapServicesAndRequests(o),
      hasAddons: o.hasAddons ?? false,
      addonDetails: (o.addonDetails ?? [])
        .map((addon) => ({
          name: addon.addonName ?? '',
          description: addon.addonDescription ?? '',
          cost: addon.addonCost ?? '',
          support: addon.addonSupport ?? false,
          url: addon.addonURL ?? '',
        }))
        .filter((addon) => addon.name || addon.description || addon.url),
      vendorName: o.vendorInfo?.vendorName ?? '',
      vendorSupport: o.vendorInfo?.vendorSupport ?? '',
      vendorUrl: o.vendorInfo?.vendorUrl ?? '',
      isExternalUseAllowed: o.isExternalUseAllowed ?? null,
      isSraRequired: o.isSraRequired ?? false,
      systemRiskAssessmentName:
        o.systemRiskAssessment?.systemRiskAssessmentName ?? '',
      supportedDataClassificationLevel: o.supportedDataClassification?.level ?? '',
      supportedDataClassificationAdditionalInformation:
        o.supportedDataClassification?.additionalInformation ?? '',
      classificationLevel: o.classification?.classificationLevel ?? '',
      isPerUserPricingAvailable: o.cost?.isPerUserPricingAvailable ?? false,
      perUserCostValue: perUserCostValueToString(o),
      perUserCostCurrency: o.cost?.perUserCost?.perUserCostCurrency ?? '',
      perUserCostFrequency: o.cost?.perUserCost?.perUserCostFrequency ?? '',
      isOtherPricingModelsAvailable: !!o.otherPricingModels?.details,
      otherPricingModels: o.otherPricingModels?.details ?? '',
      isAdditionalCostConsiderationsAvailable:
        !!o.additionalCost?.additionalCostConsiderations,
      additionalCostConsiderations:
        o.additionalCost?.additionalCostConsiderations ?? '',
      developmentCostResponsibility:
        o.developmentCostResponsibility?.responsibility ?? '',
      developmentCostResponsibilityOtherDetails:
        o.developmentCostResponsibility?.otherDetails ?? '',
      supportMaintenanceCostResponsibility:
        o.supportMaintenanceCostResponsibility?.responsibility ?? '',
      supportMaintenanceCostResponsibilityOtherDetails:
        o.supportMaintenanceCostResponsibility?.otherDetails ?? '',
      licenseCostResponsibility: o.licenseCostResponsibility ?? '',
      isSingleSignOnSupported: o.singleSignOn?.isSingleSignOnSupported ?? false,
      singleSignOnProviders: ssoToString(o.singleSignOn?.singleSignOnProviders),
      isSupported: o.isSupported ?? '',
      hosting: mapHosting(o),
      agents: mapAgents(o),
      communities: mapCommunities(o),
    },
    functionality: {
      features: mapFeatures(o),
      capabilities: mapCapabilities(o),
      useCases: mapUseCases(o),
    },
    permissions: mapPermissions(o),
  };
}

export function mapMulesoftOfferingToServiceForm(
  o: MulesoftOffering,
): FocusOfferingFormData {
  return {
    id: String(o.id ?? ''),
    knowledge: o.knowledge ?? '',
    overview: {
      displayName: o.displayName ?? '',
      approvalStatus: o.approvalStatus ?? '',
      successor: o.successor ?? '',
      description: o.description ?? '',
      similarOfferings: o.similarOfferings ?? [],
    },
    details: {
      onboardingAccessInstructions: o.onboarding?.accessInstructions ?? '',
      onboardingSteps: mapOnboardingSteps(o),
      servicesAndRequests: mapServicesAndRequests(o),
      vendorName: o.vendorInfo?.vendorName ?? '',
      vendorSupport: o.vendorInfo?.vendorSupport ?? '',
      vendorUrl: o.vendorInfo?.vendorUrl ?? '',
      isOtherPricingModelsAvailable: !!o.otherPricingModels?.details,
      otherPricingModels: o.otherPricingModels?.details ?? '',
      isAdditionalCostConsiderationsAvailable:
        !!o.additionalCost?.additionalCostConsiderations,
      additionalCostConsiderations:
        o.additionalCost?.additionalCostConsiderations ?? '',
      agents: mapAgents(o),
      communities: mapCommunities(o),
      relatedTechnology: (o.relatedTechnology ?? [])
        .map((rt) => ({
          targetId: rt.targetId ?? '',
          description: rt.description ?? '',
        }))
        .filter((rt) => rt.targetId || rt.description),
    },
    functionality: {
      capabilities: mapCapabilities(o),
      useCases: mapUseCases(o),
    },
    permissions: mapPermissions(o),
  };
}

export function mapMulesoftKnowledgeToFocusForm(
  k: MulesoftKnowledgeContent,
): FocusKnowledgeForm {
  return {
    userInstructionLongDescription: k.userInstructionLongDescription ?? '',
    integrationLongDescription: k.integrationLongDescription ?? '',
    userInstructionsVisible: k.userInstructionsVisible ?? false,
    bestPracticesVisible: k.bestPracticesVisible ?? false,
    learningResourcesVisible: k.learningResourcesVisible ?? false,
    technicalResourcesVisible: k.technicalResourcesVisible ?? false,
    integrationsVisible: k.integrationsVisible ?? false,
    faqVisible: k.faqVisible ?? false,
    additionalResourcesVisible: k.additionalResourcesVisible ?? false,
    successStoriesVisible: k.successStoriesVisible ?? false,
    releaseNotesVisible: k.releaseNotesVisible ?? false,
    userInstructions: (k.userInstructions ?? []) as FocusKnowledgeForm['userInstructions'],
    bestPractices: (k.bestPractices ?? []) as FocusKnowledgeForm['bestPractices'],
    learningResources: (k.learningResources ?? []) as FocusKnowledgeForm['learningResources'],
    technicalResources: (k.technicalResources ?? []) as FocusKnowledgeForm['technicalResources'],
    integrations: (k.integrations ?? []) as FocusKnowledgeForm['integrations'],
    faq: (k.faq ?? []) as FocusKnowledgeForm['faq'],
    additionalResources: (k.additionalResources ?? []) as FocusKnowledgeForm['additionalResources'],
    successStories: (k.successStories ?? []) as FocusKnowledgeForm['successStories'],
    releaseNotes: (k.releaseNotes ?? []) as FocusKnowledgeForm['releaseNotes'],
  };
}
